import React from 'react';

import BottomSheet from '@/core/components/BottomSheet';
import Modal from '@/core/components/Modal';
import ReportProfileForm from '@/core/components/ProfileActions/ReportProfileForm';
import BlockActionComponent from '@/core/components/ProfileActions/UserActions/BlockActionComponent';
import UnblockActionComponent from '@/core/components/ProfileActions/UserActions/UnblockActionComponent';
import ScrollDownItem from '@/core/components/ScrollDownItem';
import useDisclosure from '@/core/hooks/useDisclosure';
import type { ReportAction, UserAction } from '@/core/lib/new-architecture/domain/user.domain';
import { UserActionType } from '@/core/lib/new-architecture/domain/user.domain';
import { useTranslationContext } from '@/core/lib/translation/translation.context';

interface ReportActionComponentProps {
  action: ReportAction;
  onSuccess: () => void;
}

const ReportActionComponent: React.FC<ReportActionComponentProps> = ({ action, onSuccess }) => {
  const { t } = useTranslationContext(['profile.actions']);

  const { isOpen, onOpen, onClose } = useDisclosure();

  return (
    <>
      <ScrollDownItem icon="flag" content={t(action.text, { ns: 'profile.actions' })} type="button" onClick={onOpen} />
      {isOpen && (
        <Modal isOpen onClose={onClose}>
          <ReportProfileForm
            uuid={action.uuid}
            onClose={() => {
              onClose();
              onSuccess();
            }}
          />
        </Modal>
      )}
    </>
  );
};

interface ProfileActionsProps {
  isOpen: boolean;
  onClose: () => void;
  actions: UserAction[];
}

const ProfileActions: React.FC<ProfileActionsProps> = ({ isOpen, onClose, actions }) => {
  const renderActions = actions.reduce((acc, action) => {
    switch (action.action) {
      case UserActionType.SHARE:
        return acc;

      case UserActionType.BLOCK:
        return [...acc, <BlockActionComponent key={action.action} action={action} onSuccess={onClose} />];

      case UserActionType.UNBLOCK:
        return [...acc, <UnblockActionComponent key={action.action} action={action} onSuccess={onClose} />];

      case UserActionType.REPORT:
        return [...acc, <ReportActionComponent key={action.action} action={action} onSuccess={onClose} />];

      default:
        return acc;
    }
  }, [] as React.ReactElement[]);

  if (renderActions.length === 0) {
    return null;
  }

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose}>
      {/* eslint-disable-next-line */}
      <div className="flex flex-col" onClick={e => e.stopPropagation()}>
        {renderActions}
      </div>
    </BottomSheet>
  );
};

export default ProfileActions;
